/**
 * Voice Services Types
 * Based on Android: services/voice/*
 *
 * Type definitions for voice services including TTS and speech recognition.
 */

// ============================================================================
// Text-to-Speech Types
// ============================================================================

export interface TTSState {
  isInitialized: boolean
  isSpeaking: boolean
  isPaused: boolean
  currentLanguage: string
  currentVoice: string | null
  speechRate: number
  pitch: number
  queueSize: number
  availableVoices: number
}

export type SpeechEvent =
  | { type: 'started'; utteranceId: string }
  | { type: 'done'; utteranceId: string }
  | { type: 'error'; utteranceId: string; errorCode: number }
  | { type: 'progress'; utteranceId: string; charIndex: number; charLength: number }
  | { type: 'paused' }
  | { type: 'resumed' }

export interface VoiceInfo {
  name: string
  lang: string
  localService: boolean
  default: boolean
}

export interface SpeechConfig {
  language: string
  voiceName?: string
  speechRate: number
  pitch: number
  volume: number
  queueMode: QueueMode
}

export enum QueueMode {
  FLUSH = 'FLUSH', // Stop current and clear queue
  ADD = 'ADD', // Add to queue
}

export interface SpeakResult {
  success: boolean
  utteranceId: string | null
  error: string | null
}

// ============================================================================
// Speech Recognition Types
// ============================================================================

export interface RecognitionState {
  isListening: boolean
  isProcessing: boolean
  isContinuousMode: boolean
  currentLanguage: string
  lastError: string | null
  audioLevel: number
}

export interface RecognitionResult {
  text: string
  confidence: number
  alternatives: RecognitionAlternative[]
  isFinal: boolean
  language: string
  durationMs: number
  timestamp: number
}

export interface RecognitionAlternative {
  text: string
  confidence: number
}

export interface PartialResult {
  text: string
  stability: number
  timestamp: number
}

export interface RecognitionConfig {
  language: string
  maxAlternatives: number
  interimResults: boolean
  continuous: boolean
  maxDurationSeconds: number
}

export interface SupportedLanguage {
  code: string
  name: string
}

// ============================================================================
// Default Configurations
// ============================================================================

export const defaultSpeechConfig: SpeechConfig = {
  language: 'en-US',
  speechRate: 1,
  pitch: 1,
  volume: 1,
  queueMode: QueueMode.ADD,
}

export const defaultRecognitionConfig: RecognitionConfig = {
  language: 'en-US',
  maxAlternatives: 3,
  interimResults: true,
  continuous: false,
  maxDurationSeconds: 60,
}

// ============================================================================
// Error Types
// ============================================================================

export type VoiceError =
  | { type: 'NOT_SUPPORTED'; message: string }
  | { type: 'PERMISSION_DENIED'; message: string }
  | { type: 'NETWORK_ERROR'; message: string }
  | { type: 'NO_SPEECH'; message: string }
  | { type: 'AUDIO_CAPTURE'; message: string }
  | { type: 'UNKNOWN'; message: string }

export const VoiceErrors = {
  notSupported: (feature: string): VoiceError => ({
    type: 'NOT_SUPPORTED',
    message: `${feature} is not supported in this browser`,
  }),
  permissionDenied: (): VoiceError => ({
    type: 'PERMISSION_DENIED',
    message: 'Microphone permission denied',
  }),
  networkError: (): VoiceError => ({
    type: 'NETWORK_ERROR',
    message: 'Network error during speech recognition',
  }),
  noSpeech: (): VoiceError => ({
    type: 'NO_SPEECH',
    message: 'No speech detected',
  }),
  audioCaptureError: (): VoiceError => ({
    type: 'AUDIO_CAPTURE',
    message: 'Audio capture error',
  }),
  unknown: (message: string): VoiceError => ({
    type: 'UNKNOWN',
    message,
  }),
}

// ============================================================================
// Common Languages
// ============================================================================

export const COMMON_LANGUAGES: SupportedLanguage[] = [
  { code: 'en-US', name: 'English (US)' },
  { code: 'en-GB', name: 'English (UK)' },
  { code: 'es-ES', name: 'Spanish (Spain)' },
  { code: 'es-MX', name: 'Spanish (Mexico)' },
  { code: 'fr-FR', name: 'French' },
  { code: 'de-DE', name: 'German' },
  { code: 'it-IT', name: 'Italian' },
  { code: 'pt-BR', name: 'Portuguese (Brazil)' },
  { code: 'ja-JP', name: 'Japanese' },
  { code: 'ko-KR', name: 'Korean' },
  { code: 'zh-CN', name: 'Chinese (Simplified)' },
  { code: 'zh-TW', name: 'Chinese (Traditional)' },
  { code: 'ar-SA', name: 'Arabic' },
  { code: 'hi-IN', name: 'Hindi' },
  { code: 'ru-RU', name: 'Russian' },
]
